import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { SISCard, SISCallout, SISInfluence, SISTakeaway, SISNoteImage } from "@/components/sis/SISLessonShell";
import { RomanNumeralAnalysis } from "@/components/song/RomanNumeralAnalysis";
import { YouTubeEmbed } from "@/components/song/YouTubeEmbed";
import { RagaLadder } from "@/components/RagaLadder";

export default function SouthIndian6() {
  const { lesson, course, accent } = findLesson("south-indian-songbook", "6");
  return (
    <LessonShell
      course={course}
      courseSlug="south-indian-songbook"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <YouTubeEmbed
        videoId="1VRWltmPp-A"
        title="Chamber Welcomes Thiyagaraja (Kamavardhini) — Ilaiyaraaja · live, Lydian Nadhaswaram on classical guitar"
        caption="The piece is in E, so listen for F — the ♭2, a half-step above the tonic. That note, with the ♯4 (A♯), gives Kamavardhini its colour."
      />

      <SISCard label="The big idea" title="Naming the unnameable — the 72 Melakarta system">
        <p>
          Everyday Western theory names only a handful of seven-note scales: the major scale and
          its modes, plus the harmonic and melodic minors. Anything outside that tends to get
          filed under "exotic".
        </p>
        <p>
          Carnatic theory names <strong>seventy-two</strong> parent scales.
        </p>
        <p>
          The <strong>72 Melakarta system</strong> enumerates every seven-note scale that follows
          a fixed set of rules: Sa and Pa are always present; there is exactly one Ma; one Ri and
          one Ga, with Ri below Ga; one Dha and one Ni, with Dha below Ni; and the same notes going
          up and coming down. These 72 parent scales — called <em>Melakarta ragas</em> — are
          arranged in a numbered grid. Scales that break the rules are not in it: Locrian, for
          example, has no perfect fifth, so it has no Melakarta number.
        </p>
        <p>
          The scheme was set out by the 17th-century musicologist Venkatamakhin and standardised
          later; the names in use today follow Govindacharya's version. This lesson begins your
          journey into that system, using one of its most striking ragas as the entry point.
        </p>
      </SISCard>

      <SISCard label="Kamavardhini — the 51st Melakarta" title="Four half-steps and two wide gaps">
        <p>
          <strong>Kamavardhini</strong> (also called Pantuvarali) is the 51st Melakarta raga.
          In Western interval terms, starting from any root note:
        </p>
        <ul className="ml-5 list-disc space-y-1.5">
          <li><strong>1</strong> — the root (Sa)</li>
          <li><strong>♭2</strong> — the minor second, a half-step above the root (Ri₁)</li>
          <li><strong>3</strong> — the major third (Ga₃)</li>
          <li><strong>♯4</strong> — the augmented fourth / tritone (Ma₂)</li>
          <li><strong>5</strong> — the perfect fifth (Pa)</li>
          <li><strong>♭6</strong> — the minor sixth (Dha₁)</li>
          <li><strong>7</strong> — the major seventh (Ni₃)</li>
        </ul>
        <p>
          Look at what this scale contains: a <strong>minor second</strong> (♭2 — the tightest
          interval), a <strong>major third</strong>, a <strong>tritone</strong> (♯4), a
          <strong> minor sixth</strong>, and a <strong>major seventh</strong>. Four of its seven
          steps are half-steps, and two are three-semitone gaps — tension packed into one octave.
        </p>
        <p>
          It has no everyday Western name. Its nearest relative is <strong>Mayamalavagowla</strong>
          (the 15th Melakarta) — the same scale with a perfect 4th instead of the ♯4, which Western
          books call the double harmonic major. In Hindustani music, Kamavardhini's seven notes are
          the <strong>Purvi</strong> thaat.
        </p>
      </SISCard>

      <RagaLadder
        title="Kamavardhini (51st Melakarta) — Sa = C"
        tonic="C"
        steps={[
          { swara: "Sa",  pitch: "C4",  notes: "Root — home",                          flat: false },
          { swara: "Ri₁", pitch: "Db4", notes: "♭2 — a half-step above Sa",            flat: true  },
          { swara: "Ga₃", pitch: "E4",  notes: "Major 3rd — the brightness",           flat: false },
          { swara: "Ma₂", pitch: "F#4", notes: "Tritone — augmented 4th",              flat: false },
          { swara: "Pa",  pitch: "G4",  notes: "Perfect 5th — stability",              flat: false },
          { swara: "Dha₁",pitch: "Ab4", notes: "♭6 — minor sixth",                     flat: true  },
          { swara: "Ni₃", pitch: "B4",  notes: "Major 7th — leading tone",             flat: false },
          { swara: "Sa",  pitch: "C5",  notes: "Octave — resolution",                  flat: false },
        ]}
      />

      <SISCard label="Playing the scale" title="Sa–Ri₁–Ga₃–Ma₂–Pa–Dha₁–Ni₃–Sa on the piano">
        <p>
          Starting from C on your piano, play the Kamavardhini scale ascending:
          <strong> C – D♭ – E – F♯ – G – A♭ – B – C</strong>.
        </p>
        <p>
          Notice the sequence of intervals: half-step (C to D♭), then an augmented second
          (D♭ to E — a gap of three semitones, like a very wide whole step), then a whole step
          (E to F♯), then another half-step (F♯ to G), then a half-step again (G to A♭), then
          an augmented second (A♭ to B), then a final half-step (B to C).
        </p>
        <p>
          The pattern of intervals is: <strong>H – A2 – W – H – H – A2 – H</strong>
          (H = half-step, W = whole-step, A2 = augmented second). Those two augmented seconds —
          the leaps from D♭ to E and from A♭ to B — are the scale's sonic fingerprint. They
          are what make the melody jump in that characteristic way that sounds ancient and dramatic.
        </p>
      </SISCard>

      <RomanNumeralAnalysis
        label="Chords from Jason's chart — Kamavardhini with Sa = E (the key of the piece)"
        caption="Every note of these chords is in E Kamavardhini (E F G♯ A♯ B C D♯). On the tonic, E major. On the 3rd degree, both G♯ minor (G♯ B D♯) and a major triad G♯ C D♯ — written A♭ on the chart, because the scale's 6th is C. On the 5th, B with a flattened fifth (B D♯ F). On the 6th, C augmented (C E G♯), with B as its major 7th."
        chords={[
          { chord: "E",       rn: "I",        function: "Tonic", pitches: ["E3", "G#3", "B3"] },
          { chord: "G♯m",     rn: "iii",      pitches: ["G#3", "B3", "D#4"] },
          { chord: "A♭ (G♯)", rn: "III",      pitches: ["Ab3", "C4", "Eb4"] },
          { bar: true },
          { chord: "B(♭5)",   rn: "V(♭5)",    pitches: ["B3", "D#4", "F4"] },
          { chord: "C+maj7",  rn: "♭VI+maj7", pitches: ["C4", "E4", "G#4", "B4"] },
        ]}
      />

      <SISCard label="The 72 Melakarta — how the system works" title="Mathematics of all possible scales">
        <p>
          The 72 Melakarta ragas are arranged in 12 groups of 6. The groups are called
          <em> chakras</em> (wheels). Every raga in a chakra shares the same Ri (2nd), Ga (3rd)
          and Ma (4th); within a chakra, the six ragas differ only in their Dha (6th) and Ni (7th).
          Ragas 1–36 use Ma₁; ragas 37–72 use Ma₂.
        </p>
        <p>
          The arithmetic: Ri, Ga, Dha and Ni each have three positions (written 1, 2, 3), but Ri
          must sit below Ga and Dha below Ni. That leaves six usable Ri–Ga pairs and six Dha–Ni
          pairs. Ma comes in two flavours: Ma₁ (perfect 4th) and Ma₂ (augmented 4th / tritone).
          Six × two × six = 72. Some positions share a key on the piano — Ri₂ and Ga₁ are the
          same pitch, as are Ri₃ and Ga₂ — which is why the names matter more than the keys.
        </p>
        <p>
          Kamavardhini uses <strong>Ri₁</strong> (lowest Ri = ♭2), <strong>Ga₃</strong>
          (highest Ga = major 3rd), <strong>Ma₂</strong> (tritone), <strong>Dha₁</strong>
          (lowest Dha = ♭6), and <strong>Ni₃</strong> (highest Ni = major 7th). Each of those
          choices pushes to an extreme — which is why the scale sounds so dramatic. It is a
          raga of maximum contrast.
        </p>
      </SISCard>

      <SISCard label="Kamavardhini in this lesson" title="Chamber Welcomes Thiyagaraja">
        <p>
          The piece Jason transcribes is <em>Chamber Welcomes Thiyagaraja</em>, from Ilaiyaraaja's
          1986 instrumental album <em>How to Name It?</em> — a classical-guitar piece built on
          Kamavardhini. The video above is a 2023 live performance with Lydian Nadhaswaram on
          classical guitar. Jason works it out in E, in 3/4.
        </p>
        <p>
          Western books have no common name for this exact scale, but you may know two neighbours.
          The <strong>double harmonic major</strong> is Mayamalavagowla — Kamavardhini with a
          perfect 4th instead of the ♯4. The <strong>Phrygian dominant</strong> (1 ♭2 3 4 5 ♭6 ♭7,
          the "flamenco" sound) shares Kamavardhini's ♭2, major 3rd and ♭6, but has a perfect
          4th and a ♭7.
        </p>
        <p>
          What the Melakarta scheme adds is a name and a number — 51 — so a musician can say
          exactly which seven notes they mean.
        </p>
      </SISCard>

      <SISCallout label="Practise this — learn the scale in three keys">
        Play Kamavardhini ascending and descending from C: C–D♭–E–F♯–G–A♭–B–C, then back down.
        Listen for the two augmented-second leaps (D♭ to E, A♭ to B). Now play it from G: G–A♭–B–C♯–D–E♭–F♯–G.
        Finally from D: D–E♭–F♯–G♯–A–B♭–C♯–D. Each time, sing along with your playing —
        even if just humming. The body learns scales through sound more than through finger patterns.
      </SISCallout>

      <SISInfluence artists={["Ilaiyaraaja — How to Name It? (1986)", "Lydian Nadhaswaram (classical guitar)", "Karthik Sekaran — Raga Chords: Kamavardhini"]}>
        <p>
          Listen to the whole of <em>How to Name It?</em> — Ilaiyaraaja's instrumental album
          that sets Carnatic ragas beside Western classical writing. Then compare the studio
          version of Chamber Welcomes Thiyagaraja with the live performance above. For more
          chord ideas on this raga, Karthik Sekaran's "Raga Chords" video on Kamavardhini
          (Pantuvarali) is a useful companion.
        </p>
      </SISInfluence>

      <SISNoteImage
        src="/notes/south-indian-songbook/finals/06-how-to-name-it-kamavardhini-pt1-page1.png"
        caption="Jason's chart for Chamber Welcomes Thiyagaraja — the scale as 1 ♭2 3 ♯4 5 ♭6 7 with its semitone gaps (1-3-2-1-1-3-1), key E, 3/4 time, the notes E F G♯ A♯ B C D♯, and the chords he finds in it."
      />

      <SISTakeaway>
        The 72 Melakarta system is not an academic exercise — it is a map of seven-note colour.
        The major scale (29), natural minor (20), harmonic minor (21), melodic minor (23) and
        every church mode except Locrian have a number in it. Learning even five or six of these
        ragas by ear will expand your compositional palette enormously. Kamavardhini is a
        dramatic entry point: once you have heard that ♭2, you will never unhear it — and you
        will start using it in your own music.
        That is the whole point.
      </SISTakeaway>
    </LessonShell>
  );
}
